<script lang="ts">
  import type { ManageAPI } from "~/manage-api/types";
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import CardTitle from '../CardTitle.svelte';
  import { formatDate } from "../date-utils";

  export let event: ManageAPI.Event;
  export let request_store_key: StoresKey;
  export let imageStyle: string = ""; // For custom image styling
  export let _class: string = ""; // For custom card styling
  export let borderRadius: string = "";
  export let borderThickness: string = "";
  export let borderColor: string = "";
  export let borderStyle: string = "";
  export let hoverEffect: string = ""; // translate, scale, or none
  export let showDate: boolean = true; // Default to showing date
  export let showKeywordTags: boolean = true; // Default to showing keyword tags
  export let showIntroduction: boolean = false; // Whether to show the event introduction

  const { reverse_router } = getStoresContext(request_store_key);
  
  // Use custom style props if provided, otherwise use site-wide CSS variables
  $: actualBorderRadius = borderRadius || "var(--event-card-border-radius, var(--card-border-radius, 8px))";
  $: actualBorderThickness = borderThickness || "var(--event-card-border-thickness, var(--card-border-thickness, 0))";
  $: actualBorderColor = borderColor || "var(--event-card-border-color, var(--card-border-color, transparent))";
  $: actualBorderStyle = borderStyle || "var(--event-card-border-style, var(--card-border-style, solid))";
  
  // Calculate hover transforms based on hover effect
  // When hoverEffect is empty, we'll use CSS variables from site-wide configurator
  $: hoverCardTransform = "";
  $: hoverImageTransform = "";
  $: {
    if (hoverEffect) {
      if (hoverEffect === "translate") {
        hoverCardTransform = "translateY(-5px)";
        hoverImageTransform = "none";
      } else if (hoverEffect === "scale") {
        hoverCardTransform = "none";
        hoverImageTransform = "scale(1.1)";
      } else if (hoverEffect === "none") {
        hoverCardTransform = "none";
        hoverImageTransform = "none";
      }
    }
    // When empty, we'll use CSS variables in the template
  }
  
  // Build style string from computed values if imageStyle is not provided
  $: {
    if (!imageStyle) {
      const styleProps = [];
      if (actualBorderRadius) styleProps.push(`border-radius: ${actualBorderRadius}`);
      if (actualBorderThickness && actualBorderStyle && actualBorderColor) 
        styleProps.push(`border: ${actualBorderThickness} ${actualBorderStyle} ${actualBorderColor}`);
      else {
        if (actualBorderThickness) styleProps.push(`border-width: ${actualBorderThickness}`);
        if (actualBorderStyle) styleProps.push(`border-style: ${actualBorderStyle}`);
        if (actualBorderColor) styleProps.push(`border-color: ${actualBorderColor}`);
      }
      
      if (styleProps.length > 0) {
        imageStyle = styleProps.join('; ');
      }
    }
  }
  
  $: href = $reverse_router.routeEvent(event);
</script>

<a {href} class="event-card-link" style={hoverEffect ? `--custom-card-hover-transform: ${hoverCardTransform}; --custom-image-hover-transform: ${hoverImageTransform};` : ''}>
  <div class="event-card {_class}">
    <div class="event-image" style={imageStyle}>
      {#if event.images?.[0]}
        <img src={event.images[0]} alt={event.title} />
      {:else}
        <div class="image-fallback">
          {event.title?.charAt(0).toUpperCase() || ''}
        </div>
      {/if}
    </div>
    <div class="event-content">
      {#if (showDate || showKeywordTags) && (event.publishDate || (event.keywords && event.keywords.length > 0))}
        <div class="event-meta">
          {#if showDate && event.publishDate}
            <span class="validity">
              {formatDate(event.publishDate)}
            </span>
          {:else}
            <span class="validity-placeholder"></span>
          {/if}
          {#if showKeywordTags && event.keywords?.[0]}
            <span class="store-name">{event.keywords[0]}</span>
          {/if}
        </div>
      {/if}
      <CardTitle title={event.title} {_class} />
      
      {#if showIntroduction && event.introduction}
        <div class="event-introduction">
          {@html event.introduction}
        </div>
      {/if}
    </div>
  </div>
</a>

<style lang="scss">
  .event-card-link {
    display: block;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease-in-out;
    width: 100%;
    
    &:hover {
      @media (min-width: 768px) {
        transform: var(--custom-card-hover-transform, var(--card-hover-transform, none));
        
        img, .image-fallback {
          transform: var(--custom-image-hover-transform, var(--image-hover-transform, none));
        }
      }
      text-decoration: none;
      color: inherit;
    }
  }
  
  .event-card {
    height: auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: transparent;
    width: 100%;
  }

  .event-image {
    aspect-ratio: 1;
    width: 100%;
    height: auto;
    overflow: hidden;
    position: relative;
    
    img, .image-fallback {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    .image-fallback {
      width: 100%;
      height: 100%;
      background: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      font-weight: 600;
      color: #666;
    }
  }

  .event-content {
    padding: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;

    :global(.title-container) {
      height: 36px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: block;
    }
  }

  .event-meta {
    font-family: var(--liferay-font-family-base);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    color: #666;
  }

  .validity, .validity-placeholder {
    font-size: 0.875rem;
  }

  .store-name {
    text-align: right;
    font-weight: 500;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .event-introduction {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    font-size: 0.95rem;
    line-height: 1.5;
  }
</style> 